/**
 * THE "?" help icon (CLAUDE.md "Help tips everywhere"). One component for the whole app.
 * Hover opens on desktop, tap/click opens on touch, Enter/Space opens from the keyboard, Esc closes.
 * Text comes only from src/help/helpText.ts by key; `k` is typed, so an unknown key fails the typecheck.
 * Clicks never bubble, so a tip can sit inside a clickable row or card.
 */
import { useId, useState, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { HelpCircle } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import { HELP, type HelpKey } from '../../help/helpText.js';

export function HelpTip({ k, className }: { k: HelpKey; className?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const e = HELP[k] as { title: string; text: string; example: string; rule?: string } | undefined;
  if (!e) throw new Error(`HelpTip: no help text for key "${String(k)}"`);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" aria-label={`Help: ${e.title}`} aria-describedby={open ? id : undefined} data-help-key={k}
          onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}
          onClick={(ev) => ev.stopPropagation()}
          className={cn('inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full align-middle text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}>
          <HelpCircle className="h-3.5 w-3.5" aria-hidden />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content id={id} role="tooltip" side="top" sideOffset={6} collisionPadding={12}
          onOpenAutoFocus={(ev) => ev.preventDefault()} onClick={(ev) => ev.stopPropagation()}
          className="z-[70] w-72 max-w-[calc(100vw-24px)] rounded-lg border bg-popover p-3 text-left text-sm font-normal normal-case tracking-normal text-popover-foreground shadow-pop animate-fade-in">
          <p className="font-semibold">{e.title}</p>
          <p className="mt-1 leading-snug text-muted-foreground">{e.text}</p>
          <p className="mt-2 whitespace-pre-line rounded-md bg-muted px-2 py-1.5 text-xs leading-snug"><span className="font-medium">Example: </span>{e.example}</p>
          {e.rule && <p className="mt-1.5 text-[11px] text-muted-foreground">Rule: {e.rule}</p>}
          <Popover.Arrow className="fill-popover" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

/** Text with its help icon beside it: page titles, card titles, column headers, tab labels. */
export function WithTip({ children, k, className }: { children: ReactNode; k: HelpKey; className?: string }) {
  return <span className={cn('inline-flex items-center gap-1', className)}>{children}<HelpTip k={k} /></span>;
}
